import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../lib/use-locale';
import { pagePath } from '../config/page-routes';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'cookie-consent';

type Consent = 'accepted' | 'rejected' | null;

function getStoredConsent(): Consent {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'accepted' || v === 'rejected' ? v : null;
  } catch {
    return null;
  }
}

/**
 * GDPR cookie consent banner.
 *
 * - Shown on first visit (no stored consent)
 * - Accept / Reject buttons
 * - Link to full cookie policy
 * - Persists choice in localStorage
 * - Does NOT block page rendering (non-modal, fixed bottom bar)
 *
 * When analytics are later added, check `getStoredConsent() === 'accepted'`
 * before firing tracking scripts.
 */
export function CookieConsent() {
  const [consent, setConsent] = useState<Consent>('accepted'); // SSR: assume accepted (no banner)
  const { t } = useTranslation();
  const locale = useLocale();

  useEffect(() => {
    setConsent(getStoredConsent());
  }, []);

  if (consent !== null) return null;

  const handleAccept = () => {
    localStorage.setItem(STORAGE_KEY, 'accepted');
    setConsent('accepted');
    // Upgrade GA consent on first accept
    const w = window as typeof window & { gtag?: (...args: unknown[]) => void };
    if (typeof window !== 'undefined' && typeof w.gtag === 'function') {
      w.gtag('consent', 'update', { analytics_storage: 'granted' });
    }
  };

  const handleReject = () => {
    localStorage.setItem(STORAGE_KEY, 'rejected');
    setConsent('rejected');
  };

  return (
    <div className="cookie-banner" role="dialog" aria-label={t('cookies.bannerLabel')}>
      <div className="container cookie-banner__inner">
        <p className="cookie-banner__text">
          {t('cookies.bannerText')}{' '}
          <Link to={pagePath('cookies', locale)}>{t('cookies.learnMore')}</Link>
        </p>
        <div className="cookie-banner__actions">
          <button className="button button--ghost button--sm" onClick={handleReject}>
            {t('cookies.reject')}
          </button>
          <button className="button button--primary button--sm" onClick={handleAccept}>
            {t('cookies.accept')}
          </button>
        </div>
      </div>
    </div>
  );
}
